/**
 * One HTTP layer for the whole app.
 *
 * - `api`   openapi-fetch client typed from the generated OpenAPI document
 *           (src/api/schema.d.ts, `npm run gen:api`).
 * - `http`  small typed helper for endpoints other teams are still building
 *           (types in src/api/contracts.ts).
 *
 * Both send the session cookie, add the CSRF header (`X-Smake-CSRF`, token from
 * GET /api/v2/me) to mutations and turn failures into `ApiProblem`s. A 404 that
 * is *not* problem+json means the route does not exist on this server yet
 * (`problem.notAvailable`), which pages render as a friendly "not available yet".
 */

import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

export class ApiProblem extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail?: string;
  readonly body: Record<string, unknown>;
  /** The endpoint does not exist on this server (yet). */
  readonly notAvailable: boolean;

  constructor(status: number, body: Record<string, unknown>, notAvailable = false) {
    const title = typeof body.title === "string" ? body.title : statusText(status);
    const detail = typeof body.detail === "string" ? body.detail : undefined;
    super(detail ?? title);
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.body = body;
    this.notAvailable = notAvailable;
  }

  /** Field-level validation issues from problem+json (`issues: [{ path, message }]`). */
  get issues(): { path: string; message: string }[] {
    return Array.isArray(this.body.issues) ? (this.body.issues as { path: string; message: string }[]) : [];
  }
}

function statusText(status: number): string {
  if (status === 0) return "Network error";
  if (status === 401) return "Unauthorized";
  if (status === 403) return "Forbidden";
  if (status === 404) return "Not found";
  if (status === 409) return "Conflict";
  if (status === 501) return "Not implemented";
  if (status >= 500) return "Server error";
  return "Request failed";
}

export const isProblem = (err: unknown): err is ApiProblem => err instanceof ApiProblem;
export const isNotAvailable = (err: unknown) => isProblem(err) && (err.notAvailable || err.status === 501);

// ---------------------------------------------------------------------------
// Session state shared by both clients

let csrfToken: string | null = null;
const unauthorizedListeners = new Set<() => void>();

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Called when any request answers 401 (session expired / signed out). */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function withCsrf(method: string, headers: Headers): void {
  if (MUTATING.has(method.toUpperCase()) && csrfToken) headers.set("X-Smake-CSRF", csrfToken);
}

async function problemFrom(response: Response, parsed?: unknown): Promise<ApiProblem> {
  const type = response.headers.get("content-type") ?? "";
  const isProblemJson = type.includes("application/problem+json");
  let body: Record<string, unknown> = {};
  if (parsed && typeof parsed === "object") body = parsed as Record<string, unknown>;
  else if (parsed === undefined) {
    try {
      const text = await response.text();
      if (type.includes("json") && text) body = JSON.parse(text);
    } catch {
      /* keep empty body */
    }
  }
  if (response.status === 401) for (const l of unauthorizedListeners) l();
  const notAvailable = response.status === 404 && !isProblemJson;
  return new ApiProblem(response.status, body, notAvailable);
}

// ---------------------------------------------------------------------------
// Typed client for the generated schema

const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    withCsrf(request.method, request.headers);
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(csrfMiddleware);

/** `await unwrap(api.GET("/api/v2/me"))` → data, or throws `ApiProblem`. */
export async function unwrap<T>(pending: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await pending;
  } catch (err) {
    throw new ApiProblem(0, { title: "Network error", detail: (err as Error)?.message ?? "The server could not be reached." });
  }
  if (!result.response.ok) throw await problemFrom(result.response, result.error ?? null);
  return result.data as T;
}

// ---------------------------------------------------------------------------
// Helper for contract (not yet generated) endpoints

type Query = Record<string, string | number | boolean | string[] | undefined | null>;

export function buildUrl(path: string, query?: Query): string {
  const url = path.startsWith("/") ? path : `/api/v2/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, Array.isArray(value) ? value.join(",") : String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(method: string, path: string, options: { query?: Query; body?: unknown; form?: FormData } = {}): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });
  withCsrf(method, headers);
  let body: BodyInit | undefined;
  if (options.form) body = options.form;
  else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), { method, headers, body, credentials: "same-origin" });
  } catch (err) {
    throw new ApiProblem(0, { title: "Network error", detail: (err as Error)?.message ?? "The server could not be reached." });
  }
  if (!response.ok) throw await problemFrom(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const http = {
  get: <T>(path: string, query?: Query) => request<T>("GET", path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>("POST", path, { body: body ?? {}, query }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body: body ?? {} }),
  del: <T = void>(path: string, query?: Query) => request<T>("DELETE", path, { query }),
  upload: <T>(path: string, form: FormData) => request<T>("POST", path, { form }),
  /**
   * A POST answered as a stream (server-sent events; read it with lib/sse.ts): the response once it
   * is ok, else the `ApiProblem` (a refusal before the stream starts is plain problem+json).
   */
  async stream(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    const headers = new Headers({ Accept: "text/event-stream", "Content-Type": "application/json" });
    withCsrf("POST", headers);
    let response: Response;
    try {
      response = await fetch(buildUrl(path), { method: "POST", headers, body: JSON.stringify(body), credentials: "same-origin", signal });
    } catch (err) {
      if (signal?.aborted) throw err;
      throw new ApiProblem(0, { title: "Network error", detail: (err as Error)?.message ?? "The server could not be reached." });
    }
    if (!response.ok) throw await problemFrom(response);
    return response;
  },
};

/** Sign out: ends the local session and returns Keycloak's end-session URL. */
export async function signOut(): Promise<void> {
  const headers = new Headers();
  withCsrf("POST", headers);
  try {
    const res = await fetch("/auth/logout", { method: "POST", headers, credentials: "same-origin" });
    const body = (await res.json().catch(() => ({}))) as { logoutUrl?: string };
    window.location.assign(body.logoutUrl ?? "/");
  } catch {
    window.location.assign("/");
  }
}

export function signInUrl(returnTo: string = window.location.pathname + window.location.search): string {
  return `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}
